import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { InputSearchComponent, UserAvatarComponent } from '@aionzdev/design-system';
import { debounceTime, fromEvent, Subject, takeUntil } from 'rxjs';
import { BuyerItem } from '../../../interfaces/buyer-item.interface';
import { OrderBy, OrderByColumn, OrderDirection } from '../../../interfaces/find-purchase-request.dto';
import { PurchaseRequestBranch } from '../../../interfaces/purchase-request-branch';

/** Um filtro em uso, mostrado como chip removível abaixo dos campos. */
interface ActiveFilterChip {
  kind: 'branch' | 'buyer' | 'order' | 'search';
  /** Ex.: "Endereço" */
  name: string;
  /** Ex.: "Matriz" */
  value: string;
}

@Component({
  selector: 'aionz-purchasing-filters-toolbar',
  imports: [
    MatSelectModule,
    MatButtonModule,
    MatChipsModule,
    MatIconModule,
    UserAvatarComponent,
    InputSearchComponent,
    ReactiveFormsModule
  ],
  templateUrl: './purchasing-filters-toolbar.component.html',
  styleUrl: './purchasing-filters-toolbar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PurchasingFiltersToolbarComponent {
  private _destroyRef = inject(DestroyRef);

  public branches = input<PurchaseRequestBranch[]>([]);
  public buyers = input<BuyerItem[]>([]);
  public isBuyersLoading = input<boolean>(false);
  public selectedBranchId = input<string | undefined>('all');
  public selectedBuyerId = input<string | undefined>('all');
  public selectedOrderBy = input<OrderBy | undefined>(undefined);
  /** Termo vindo da página; mantém o campo em sincronia quando a página limpa os filtros. */
  public searchTerm = input<string>('');

  public branchChange = output<string>();
  public buyerChange = output<string>();
  public orderChange = output<OrderBy | undefined>();
  public buyersScrolled = output<void>();
  public searchChange = output<string>();
  /** "Limpar filtros": a página zera endereço, comprador, ordenação e busca de uma vez. */
  public clearFilters = output<void>();

  public searchControl = new FormControl<string>('', { nonNullable: true });

  constructor() {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this._destroyRef))
      .subscribe(term => this.searchChange.emit(term));

    effect(() => {
      const term = this.searchTerm();
      if (term !== this.searchControl.value) {
        this.searchControl.setValue(term, { emitEvent: false });
      }
    });
  }

  protected readonly orderOptions: { label: string; key: OrderByColumn; order: OrderDirection }[] = [
    { label: 'Atualização mais recente', key: OrderByColumn.UPDATED_AT, order: 'desc' },
    { label: 'Atualização mais antiga', key: OrderByColumn.UPDATED_AT, order: 'asc' },
    { label: 'Solicitação mais recente', key: OrderByColumn.CREATED_AT, order: 'desc' },
    { label: 'Solicitação mais antiga', key: OrderByColumn.CREATED_AT, order: 'asc' },
    { label: 'Entrega mais próxima', key: OrderByColumn.EXPECTED_DELIVERY_DATE, order: 'asc' },
    { label: 'Entrega mais distante', key: OrderByColumn.EXPECTED_DELIVERY_DATE, order: 'desc' }
  ];

  /** Atalho para o filtro de comprador "Atribuídos a mim", fora do select. */
  protected readonly isMineActive = computed(() => this.selectedBuyerId() === 'me');

  protected toggleMine(): void {
    this.buyerChange.emit(this.isMineActive() ? 'all' : 'me');
  }

  protected readonly selectedOrderValue = computed(() => {
    const order = this.selectedOrderBy();
    return order ? this.orderOptionValue(order) : 'default';
  });

  /** Filtros em uso, na ordem dos campos. Vazio quando tudo está no padrão. */
  protected readonly activeFilters = computed<ActiveFilterChip[]>(() => {
    const chips: ActiveFilterChip[] = [];

    const branchId = this.selectedBranchId();
    if (branchId && branchId !== 'all') {
      const branch = this.branches().find(b => b.id.toString() === branchId);
      chips.push({ kind: 'branch', name: 'Endereço', value: branch?.name ?? branchId });
    }

    const buyerId = this.selectedBuyerId() ?? 'all';
    if (buyerId !== 'all') {
      const buyer = this.buyers().find(b => b.id === buyerId);
      const value = buyerId === 'me' ? 'Atribuídos a mim' : (buyer?.name ?? 'Comprador selecionado');
      chips.push({ kind: 'buyer', name: 'Comprador', value });
    }

    const order = this.selectedOrderBy();
    if (order) {
      const option = this.orderOptions.find(o => o.key === order.key && o.order === order.order);
      chips.push({ kind: 'order', name: 'Ordem', value: option?.label ?? `${order.key} ${order.order}` });
    }

    const term = this.searchTerm().trim();
    if (term) {
      chips.push({ kind: 'search', name: 'Busca', value: term });
    }

    return chips;
  });

  private buyerSelect = viewChild<MatSelect>('buyerSelect');
  private buyerPanelClosed$ = new Subject<void>();

  protected orderOptionValue(o: { key: OrderByColumn; order: OrderDirection }): string {
    return `${o.key}:${o.order}`;
  }

  private parseOrderOptionValue(value: string): OrderBy {
    const [key, order] = value.split(':');
    return { key: key as OrderByColumn, order: order as OrderDirection };
  }

  protected onBranchChange(value: string): void {
    this.branchChange.emit(value);
  }

  protected onBuyerChange(value: string): void {
    this.buyerChange.emit(value);
  }

  protected onOrderChange(value: string): void {
    if (value === 'default') {
      this.orderChange.emit(undefined);
      return;
    }

    this.orderChange.emit(this.parseOrderOptionValue(value));
  }

  /** O "x" de um chip volta só aquele filtro ao padrão. */
  protected removeFilter(chip: ActiveFilterChip): void {
    switch (chip.kind) {
      case 'branch':
        this.branchChange.emit('all');
        break;
      case 'buyer':
        this.buyerChange.emit('all');
        break;
      case 'order':
        this.orderChange.emit(undefined);
        break;
      case 'search':
        // setValue com evento: passa pelo debounce e chega à página como searchChange('')
        this.searchControl.setValue('');
        break;
    }
  }

  protected onClearFilters(): void {
    this.searchControl.setValue('', { emitEvent: false });
    this.clearFilters.emit();
  }

  protected onBuyerSelectOpened(): void {
    const panelEl = (this.buyerSelect()?.panel?.nativeElement as HTMLElement | undefined);
    if (!panelEl) return;

    fromEvent(panelEl, 'scroll')
      .pipe(takeUntil(this.buyerPanelClosed$))
      .subscribe(() => {
        const { scrollTop, scrollHeight, clientHeight } = panelEl;
        if (scrollHeight - scrollTop - clientHeight < 48) {
          this.buyersScrolled.emit();
        }
      });
  }

  protected onBuyerSelectClosed(): void {
    this.buyerPanelClosed$.next();
  }
}
